import React, { Component } from 'react'
import hoistNonReactStatic from 'hoist-non-react-statics'
import { WithVideoChatProps } from '@deal/chat-firebase'
import { graphql, MutationFunc, compose } from 'react-apollo'
import {
  JoinVideoChatInput,
  JoinVideoChatMutation,
  JoinVideoChatMutationVariables,
  DeclineVideoChatFromInvitationMutation,
  DeclineVideoChatFromInvitationMutationVariables,
  DeclineVideoChatInput
} from '#src/generated/graphql'
import JoinVideoChat from './JoinVideoChat.gql'
import DeclineVideoChat from './DeclineVideoChat.gql'
import ringAduioUrl from './ring.mp3'

function getDisplayName(WrappedComponent: React.ComponentType<any>) {
  return WrappedComponent.displayName || WrappedComponent.name || 'Component'
}

function withVideoChat<P>(
  WrappedComponent: React.ComponentType<P & WithVideoChatProps>
): React.ComponentType<P> {
  class WithVideoChat extends Component<P & Mutations> {
    public static displayName = `WithVideoChat(${getDisplayName(WrappedComponent)})`
    public constructor(props: P & Mutations) {
      super(props)
      this.joinVideoChat = this.joinVideoChat.bind(this)
      this.declineVideoChat = this.declineVideoChat.bind(this)
    }

    public joinVideoChat(externalConversationId: string, messagingUserId: string): Promise<string> {
      const input: JoinVideoChatInput = {
        externalConversationId: externalConversationId,
        messagingUserId: messagingUserId
      }
      // @ts-ignore
      return this.props.joinVideoChat!({
        variables: {
          input: input
        }
        // @ts-ignore
      }).then(request => {
        if (!request || !request.data || !request.data.joinVideoChat.token) {
          throw new Error('server error')
        }

        return request.data.joinVideoChat.token
      })
    }

    public declineVideoChat(
      externalConversationId: string,
      messagingUserId: string
    ): Promise<boolean> {
      const input: DeclineVideoChatInput = {
        externalConversationId: externalConversationId,
        messagingUserId: messagingUserId
      }
      // @ts-ignore
      return this.props.declineVideoChat!({
        variables: {
          input: input
        }
        // @ts-ignore
      }).then(request => !!request && !!request.data && request.data.declineVideoChat.success)
    }

    public render() {
      return (
        <WrappedComponent
          {...this.props}
          ringAudioUrl={ringAduioUrl}
          startVideoChat={this.joinVideoChat}
          joinVideoChat={this.joinVideoChat}
          declineVideoChat={this.declineVideoChat}
        />
      )
    }
  }

  interface Mutations {
    declineVideoChat?: MutationFunc<
      DeclineVideoChatFromInvitationMutation,
      DeclineVideoChatFromInvitationMutationVariables
    >
    joinVideoChat?: MutationFunc<JoinVideoChatMutation, JoinVideoChatMutationVariables>
  }

  const WithVideoChatWrapper = compose(
    graphql<{}, P>(JoinVideoChat, { name: 'joinVideoChat' }),
    graphql<{}, P>(DeclineVideoChat, { name: 'declineVideoChat' })
  )(WithVideoChat)

  return hoistNonReactStatic<P, WithVideoChatProps>(WithVideoChatWrapper, WrappedComponent)
}

export { withVideoChat }
